import styled from 'styled-components';
import { CloseIcon } from '../TipSheetIcons/close-icon';
import { ArchiveFilterChoices } from './ArchiveFilterChoices.jsx';
import { ArchiveKeywordSearch } from './ArchiveKeywordSearch.jsx';
import { ArchiveTopicsFilter } from './ArchiveTopicsFilter.jsx';
import { ArchiveBillFilter } from './ArchiveBillFilter.jsx';
import { ArchiveLawmakerSelector } from './ArchiveLawmakerSelector.jsx';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 85%;
  max-width: 400px;
  background: #fff;
  z-index: 101;
  transform: ${({ $isOpen }) =>
    $isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
  color: #0E4C60;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Content = styled.div`
  padding: 1rem;
  flex: 1 1 auto;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const YearLabel = styled.div`
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

const YearRadio = styled.label`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  cursor: pointer;

  input[type='radio'] {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  .custom-checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #cccccc;
    background: #fafafa;
    display: inline-block;
    position: relative;
    margin-right: 0.5rem;
    transition: background 0.2s;
    box-shadow: 0px 2px 3px 0px #0000000d inset;
  }

  input[type='radio']:checked + .custom-checkbox {
    background: #0a819f;
    border-color: #0a819f;
  }

  .custom-checkbox:after {
    content: '';
    position: absolute;
    display: none;
  }

  input[type='radio']:checked + .custom-checkbox:after {
    display: block;
  }

  .custom-checkbox:after {
    left: 5px;
    top: -1px;
    width: 6px;
    height: 12px;
    border: solid #fff;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    content: '';
    position: absolute;
  }
`;

export const ArchiveOffCanvasFilter = ({
  isOpen,
  setIsOpen,
  sessionYear,
  setSessionYear,
  keywords,
  setKeywords,
  performSearch,
  topics,
  bills,
  legislators,
  selectedTopics,
  setSelectedTopics,
  selectedBills,
  setSelectedBills,
  selectedLegislators,
  setSelectedLegislators,
  uniqueYears,
}) => {
  // Helper for search with current state
  const doSearch = ({
    newKeywords = keywords,
    newSessionYear = sessionYear,
    newBills = selectedBills,
    newLegislators = selectedLegislators,
    newTopics = selectedTopics,
  } = {}) => {
    performSearch(
      newKeywords,
      newSessionYear,
      newBills,
      newLegislators,
      newTopics
    );
    setIsOpen(false);
  };

  return (
    <>
      <Overlay $isOpen={isOpen} onClick={() => setIsOpen(false)} />
      <Drawer $isOpen={isOpen}>
        <Header>
          <Title>Filter Archive</Title>
          <CloseBtn onClick={() => setIsOpen(false)} aria-label="Close filters">
            <CloseIcon />
          </CloseBtn>
        </Header>
        <Content>
          <ArchiveFilterChoices
            tags={selectedTopics}
            setTags={setSelectedTopics}
            legislators={selectedLegislators}
            setLegislators={setSelectedLegislators}
            selectedBills={selectedBills}
            setSelectedBills={setSelectedBills}
            keywords={keywords}
            setKeywords={setKeywords}
            setSearchActive={() => {}}
            setSearchStories={() => {}}
            searchInputRef={null}
            originalStories={[]}
            chamberFilter="both"
            setChamberFilter={() => {}}
            performSearch={doSearch}
            variant="mobile"
          />
          <div>
            <YearLabel>Year</YearLabel>
            {uniqueYears.map((year) => (
              <YearRadio key={`offcanvas-${year.value}`}>
                <input
                  type="radio"
                  checked={sessionYear[0] === year.value}
                  onChange={() => {
                    setSessionYear([year.value]);
                    doSearch({ newSessionYear: [year.value] });
                  }}
                  id={`active-year-${year.value}`}
                  name="activeSessionYear"
                />
                <span className="custom-checkbox"></span>
                {year.label}
              </YearRadio>
            ))}
          </div>
          <div>
            <ArchiveKeywordSearch
              keywords={keywords}
              setKeywords={(newKeywords) => {
                setKeywords(newKeywords);
                doSearch({ newKeywords });
              }}
              isMobile={true}
              isActive={true}
            />
          </div>
          <div>
            <ArchiveTopicsFilter
              topics={topics}
              selectedTopics={selectedTopics}
              setSelectedTopics={(newTopics) => {
                setSelectedTopics(newTopics);
                doSearch({ newTopics });
              }}
              isMobile={true}
              isActive={true}
            />
          </div>
          <div>
            <ArchiveBillFilter
              bills={bills}
              selectedBills={selectedBills}
              setSelectedBills={(newBills) => {
                setSelectedBills(newBills);
                doSearch({ newBills });
              }}
              isMobile={true}
              isActive={true}
            />
          </div>
          <div>
            <ArchiveLawmakerSelector
              legislators={legislators}
              selectedLegislators={selectedLegislators}
              setSelectedLegislators={(newLegislators) => {
                setSelectedLegislators(newLegislators);
                doSearch({ newLegislators });
              }}
              isMobile={true}
              isActive={true}
            />
          </div>
        </Content>
      </Drawer>
    </>
  );
};
